import { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { apiRequest, apiRoutes } from '../../../api';
import appConfig from '../../../config/appConfig';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const TOKEN = () => localStorage.getItem(appConfig.auth.accessTokenKey);
async function apiFetch(path) {
    const token = TOKEN();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const r = await apiRequest(path, { headers });
    if (!r.ok) throw new Error(`${r.status} ${path}`);
    return r.json();
}

function toGeometry(b) {
    if (!b) return null;
    if (typeof b === 'string') { try { return JSON.parse(b); } catch { return null; } }
    return b;
}

function getCentroid(boundary) {
    const geom = toGeometry(boundary);
    if (!geom) return null;
    const coords = geom.type === 'MultiPolygon'
        ? geom.coordinates[0][0]
        : geom.type === 'Polygon' ? geom.coordinates[0] : null;
    if (!coords?.length) return null;
    let lat = 0, lng = 0;
    coords.forEach(([lo, la]) => { lng += lo; lat += la; });
    return [lat / coords.length, lng / coords.length];
}

function pointInPolygon(point, polygon) {
    const [py, px] = point;
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [iy, ix] = polygon[i];
        const [jy, jx] = polygon[j];
        if (((iy > py) !== (jy > py)) && px < ((jx - ix) * (py - iy)) / (jy - iy) + ix)
            inside = !inside;
    }
    return inside;
}

function useDebounce(val, ms) {
    const [dv, setDv] = useState(val);
    useEffect(() => {
        const t = setTimeout(() => setDv(val), ms);
        return () => clearTimeout(t);
    }, [val, ms]);
    return dv;
}

function FlyTo({ target }) {
    const map = useMap();
    useEffect(() => {
        if (!target) return;
        if (target.bbox) {
            const { min_lat, min_lon, max_lat, max_lon } = target.bbox;
            map.fitBounds([[min_lat, min_lon], [max_lat, max_lon]], { padding: [24, 24] });
        } else if (target.center) {
            const lat = target.center.lat ?? target.center[0];
            const lon = target.center.lon ?? target.center[1];
            map.setView([lat, lon], 13);
        } else if (Array.isArray(target)) {
            map.setView(target, 13);
        }
    }, [target, map]);
    return null;
}

function ClickHandler({ onMapClick, drawMode }) {
    useMapEvents({
        click(e) { if (!drawMode) onMapClick(e.latlng.lat, e.latlng.lng); }
    });
    return null;
}

function NeighborhoodLayer({ neighborhoods, selectedIds, onToggle, drawMode }) {
    const style = useCallback((feature) => {
        const sel = selectedIds.has(feature.properties.id);
        return {
            color: sel ? '#3949ab' : '#78909c',
            weight: sel ? 2.5 : 1,
            fillColor: sel ? '#3949ab' : '#cfd8dc',
            fillOpacity: sel ? 0.4 : 0.12,
        };
    }, [selectedIds, drawMode]);

    const onEachFeature = useCallback((feature, layer) => {
        layer.bindTooltip(feature.properties.name, { sticky: true });
        layer.on('click', (e) => {
            if (!drawMode) { L.DomEvent.stopPropagation(e); onToggle(feature.properties); }
        });
    }, [onToggle, drawMode]);

    const features = neighborhoods.map(n => {
        const geom = toGeometry(n.boundary);
        if (!geom) return null;
        return { type: 'Feature', properties: { id: n.id, name: n.name }, geometry: geom };
    }).filter(Boolean);

    if (!features.length) return null;
    return (
        <GeoJSON
            key={neighborhoods.map(n => n.id).join() + [...selectedIds].join() + String(drawMode)}
            data={{ type: 'FeatureCollection', features }}
            style={style}
            onEachFeature={onEachFeature}
        />
    );
}

function DrawLayer({ drawMode, onDrawComplete }) {
    const map = useMap();
    const drawing = useRef(false);
    const pts = useRef([]);
    const lineRef = useRef(null);

    useEffect(() => {
        if (drawMode) {
            map.dragging.disable();
            map.getContainer().style.cursor = 'crosshair';
        } else {
            map.dragging.enable();
            map.getContainer().style.cursor = '';
            if (lineRef.current) { map.removeLayer(lineRef.current); lineRef.current = null; }
            pts.current = [];
            drawing.current = false;
        }
    }, [drawMode, map]);

    useMapEvents({
        mousedown(e) {
            if (!drawMode) return;
            drawing.current = true;
            pts.current = [[e.latlng.lat, e.latlng.lng]];
        },
        mousemove(e) {
            if (!drawMode || !drawing.current) return;
            pts.current.push([e.latlng.lat, e.latlng.lng]);
            if (lineRef.current) map.removeLayer(lineRef.current);
            lineRef.current = L.polyline(pts.current, {
                color: '#6a1b9a', weight: 2.5, dashArray: '6 4'
            }).addTo(map);
        },
        mouseup() {
            if (!drawMode || !drawing.current) return;
            drawing.current = false;
            if (lineRef.current) { map.removeLayer(lineRef.current); lineRef.current = null; }
            const p = pts.current;
            pts.current = [];
            if (p.length > 3) {
                const flash = L.polygon([...p, p[0]], {
                    color: '#6a1b9a', fillOpacity: 0.12, dashArray: '5 3'
                }).addTo(map);
                setTimeout(() => map.removeLayer(flash), 1200);
                onDrawComplete(p);
            }
        }
    });
    return null;
}

export default function Step2_LocationAndNeighborhoods({ data, onChange, onNext, onBack }) {
    const [errors, setErrors] = useState({});

    const [provinces, setProvinces] = useState([]);
    const [cities, setCities] = useState([]);
    const [regions, setRegions] = useState([]);
    const [loadingProv, setLoadingProv] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);

    const [cityNbrs, setCityNbrs] = useState([]);
    const [loadingNbrs, setLoadingNbrs] = useState(false);
    const [selRegion, setSelRegion] = useState('');
    const [search, setSearch] = useState('');
    const dSearch = useDebounce(search, 250);

    const [selectedMap, setSelectedMap] = useState(() => {
        const m = new Map();
        (data.preferred_neighborhoods || []).forEach(n => {
            if (n && typeof n === 'object') m.set(n.id, n);
        });
        return m;
    });

    const [homePin, setHomePin] = useState(
        data.lat && data.lon ? [data.lat, data.lon] : null
    );
    const [flyTarget, setFlyTarget] = useState(null);
    const [drawMode, setDrawMode] = useState(false);
    const [lastDrawCount, setLastDrawCount] = useState(0);

    // load provinces
    useEffect(() => {
        setLoadingProv(true);
        apiFetch(apiRoutes.locations.provinces)
            .then(d => setProvinces(Array.isArray(d) ? d : d.results ?? []))
            .catch(() => {})
            .finally(() => setLoadingProv(false));
    }, []);

    // load cities when province changes
    useEffect(() => {
        if (!data.province_id) { setCities([]); return; }
        setLoadingCities(true);
        apiFetch(apiRoutes.locations.provinceCities(data.province_id))
            .then(d => setCities(Array.isArray(d) ? d : d.results ?? []))
            .catch(() => {})
            .finally(() => setLoadingCities(false));
    }, [data.province_id]);

    // load regions + neighborhoods when city changes
    useEffect(() => {
        if (!data.city_id) { setRegions([]); setSelRegion(''); setCityNbrs([]); return; }
        apiFetch(apiRoutes.locations.regionsByCity(data.city_id))
            .then(d => setRegions(Array.isArray(d) ? d : d.results ?? []))
            .catch(() => {});
        setSelRegion('');
        setLoadingNbrs(true);
        apiFetch(`${apiRoutes.locations.neighborhoods}?city=${data.city_id}&page_size=500`)
            .then(d => {
                const list = Array.isArray(d) ? d : d.results ?? [];
                setCityNbrs(list);
                let mnLa = Infinity, mnLo = Infinity, mxLa = -Infinity, mxLo = -Infinity;
                list.forEach(n => {
                    if (!n.bbox) return;
                    mnLa = Math.min(mnLa, n.bbox.min_lat); mnLo = Math.min(mnLo, n.bbox.min_lon);
                    mxLa = Math.max(mxLa, n.bbox.max_lat); mxLo = Math.max(mxLo, n.bbox.max_lon);
                });
                if (mnLa !== Infinity)
                    setFlyTarget({ bbox: { min_lat: mnLa, min_lon: mnLo, max_lat: mxLa, max_lon: mxLo } });
            })
            .catch(() => {})
            .finally(() => setLoadingNbrs(false));
    }, [data.city_id]);

    // region filter zoom
    useEffect(() => {
        if (!selRegion) return;
        const r = regions.find(r => String(r.id) === String(selRegion));
        if (r?.bbox) setFlyTarget({ bbox: r.bbox });
        else if (r?.center) setFlyTarget({ center: r.center });
    }, [selRegion, regions]);

    const handleProvinceChange = (e) => {
        const prov = provinces.find(p => String(p.id) === e.target.value);
        onChange({ ...data, province_id: e.target.value, province: prov?.name || '', city_id: '', city: '' });
        if (prov?.center) setFlyTarget({ center: prov.center });
    };

    const handleCityChange = (e) => {
        const city = cities.find(c => String(c.id) === e.target.value);
        onChange({ ...data, city_id: e.target.value, city: city?.name || '' });
        if (city?.coordinates)
            setFlyTarget({ center: { lat: city.coordinates.lat, lon: city.coordinates.lon } });
    };

    const handleMapClick = useCallback(async (lat, lon) => {
        setHomePin([lat, lon]);
        onChange({ ...data, lat, lon });
        setFlyTarget([lat, lon]);
        try {
            const token = TOKEN();
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers.Authorization = `Bearer ${token}`;
            const r = await apiRequest(apiRoutes.locations.findNeighborhoodByPoint, {
                method: 'POST',
                headers,
                body: JSON.stringify({ lat, lon })
            });
            if (r.ok) {
                const d = await r.json();
                if (d.found && d.neighborhood) {
                    const n = d.neighborhood;
                    setSelectedMap(prev => {
                        if (prev.has(n.id)) return prev;
                        const next = new Map(prev);
                        next.set(n.id, n);
                        return next;
                    });
                }
            }
        } catch { /* silent */ }
    }, [data, onChange]);

    const toggle = useCallback((nbr) => {
        setSelectedMap(prev => {
            const next = new Map(prev);
            if (next.has(nbr.id)) next.delete(nbr.id);
            else { next.set(nbr.id, nbr); }
            return next;
        });
    }, []);

    const handleDrawComplete = useCallback((polygonPoints) => {
        setDrawMode(false);
        const hits = cityNbrs.filter(n => {
            const c = getCentroid(n.boundary);
            return c && pointInPolygon(c, polygonPoints);
        });
        if (!hits.length) return;
        setSelectedMap(prev => {
            const next = new Map(prev);
            hits.forEach(n => next.set(n.id, n));
            return next;
        });
        setLastDrawCount(hits.length);
        setTimeout(() => setLastDrawCount(0), 3000);
    }, [cityNbrs]);

    const selectedIds = new Set(selectedMap.keys());

    const visibleList = cityNbrs.filter(n => {
        const matchRegion = !selRegion || String(n.region) === String(selRegion);
        const matchSearch = !dSearch.trim() || n.name.includes(dSearch.trim());
        return matchRegion && matchSearch;
    });

    const validate = () => {
        const e = {};
        if (!data.province_id) e.province = 'استان الزامی است';
        if (!data.city_id) e.city = 'شهر الزامی است';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleNext = () => {
        if (!validate()) return;
        onChange({ ...data, preferred_neighborhoods: [...selectedMap.values()] });
        onNext();
    };

    const mapCenter = homePin || [35.6892, 51.389];


    return (
        <div>
            <h2 className="aaw-step-title">موقعیت و محله‌های فعالیت</h2>
            <p className="aaw-step-desc">
                استان و شهر خود را انتخاب کنید، روی نقشه محل خانه‌تان را مشخص کنید،
                سپس محله‌هایی که می‌خواهید در آن‌ها فعالیت کنید را انتخاب نمایید.
            </p>

            <div className="aaw-row">
                <div className="aaw-field">
                    <label className="aaw-label">استان *</label>
                    <select
                        className={`aaw-select ${errors.province ? 'error' : ''}`}
                        value={data.province_id || ''}
                        onChange={handleProvinceChange}
                        disabled={loadingProv}
                    >
                        <option value="">{loadingProv ? 'در حال بارگذاری...' : 'انتخاب استان'}</option>
                        {provinces.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    {errors.province && <span className="aaw-field-error">{errors.province}</span>}
                </div>
                <div className="aaw-field">
                    <label className="aaw-label">شهر *</label>
                    <select
                        className={`aaw-select ${errors.city ? 'error' : ''}`}
                        value={data.city_id || ''}
                        onChange={handleCityChange}
                        disabled={loadingCities || !data.province_id}
                    >
                        <option value="">{loadingCities ? 'در حال بارگذاری...' : 'انتخاب شهر'}</option>
                        {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    {errors.city && <span className="aaw-field-error">{errors.city}</span>}
                </div>
            </div>

            <div className="aaw-field">
                <label className="aaw-label">
                    آدرس دقیق <span style={{ fontWeight: 400, color: '#90a4ae', fontSize: '0.78rem' }}>(اختیاری)</span>
                </label>
                <input
                    className="aaw-input"
                    value={data.address || ''}
                    onChange={e => onChange({ ...data, address: e.target.value })}
                    placeholder="خیابان، کوچه، پلاک..."
                />
            </div>

            <div className="s5-layout" style={{ marginTop: 16 }}>

                {/* left panel */}
                <div className="s5-panel">
                    <div className="aaw-field">
                        <label className="aaw-label">
                            منطقه <span style={{ fontWeight: 400, color: '#90a4ae', fontSize: '0.78rem' }}>(فیلتر لیست)</span>
                        </label>
                        <select
                            className="aaw-select"
                            value={selRegion}
                            onChange={e => setSelRegion(e.target.value)}
                            disabled={!data.city_id || !regions.length}
                        >
                            <option value="">همه مناطق</option>
                            {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                    </div>

                    {data.city_id && (
                        <input
                            className="aaw-input"
                            placeholder="جستجوی محله..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{ marginBottom: 8 }}
                        />
                    )}

                    {loadingNbrs && (
                        <p style={{ fontSize: '0.82rem', color: '#90a4ae', textAlign: 'center', padding: 12 }}>
                            در حال بارگذاری محله‌ها...
                        </p>
                    )}

                    {visibleList.length > 0 && (
                        <ul className="s5-list">
                            {visibleList.map(n => (
                                <li
                                    key={n.id}
                                    className={`s5-item ${selectedIds.has(n.id) ? 's5-item-sel' : ''}`}
                                    onClick={() => toggle(n)}
                                >
                                    <span className="s5-check">{selectedIds.has(n.id) ? '✓' : ''}</span>
                                    {n.name}
                                </li>
                            ))}
                        </ul>
                    )}

                    {!data.city_id && !loadingNbrs && (
                        <p className="s5-hint-text">ابتدا استان و شهر را انتخاب کنید.</p>
                    )}

                    {selectedMap.size > 0 && (
                        <div className="s5-chips" style={{ marginTop: 10 }}>
                            {[...selectedMap.values()].map(n => (
                                <span key={n.id} className="s5-chip">
                                    {n.name}
                                    <button type="button" onClick={() => toggle(n)}>✕</button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* map */}
                <div className="s5-map-wrap">
                    {cityNbrs.length > 0 && (
                        <div className="s5-map-toolbar">
                            <button
                                type="button"
                                className={`s5-draw-btn ${drawMode ? 's5-draw-btn-active' : ''}`}
                                onClick={() => setDrawMode(m => !m)}
                            >
                                {drawMode ? (
                                    <>
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 15, height: 15 }}>
                                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                        </svg>
                                        لغو رسم
                                    </>
                                ) : (
                                    <>
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 15, height: 15 }}>
                                            <path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
                                        </svg>
                                        رسم محدوده
                                    </>
                                )}
                            </button>
                            {lastDrawCount > 0 && (
                                <span className="s5-draw-feedback">✓ {lastDrawCount} محله انتخاب شد</span>
                            )}
                        </div>
                    )}

                    <MapContainer center={mapCenter} zoom={11} className="s5-map">
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; OpenStreetMap contributors'
                        />
                        <ClickHandler onMapClick={handleMapClick} drawMode={drawMode} />
                        <NeighborhoodLayer
                            neighborhoods={cityNbrs}
                            selectedIds={selectedIds}
                            onToggle={toggle}
                            drawMode={drawMode}
                        />
                        <DrawLayer drawMode={drawMode} onDrawComplete={handleDrawComplete} />
                        <FlyTo target={flyTarget} />
                        {homePin && <Marker position={homePin} />}
                    </MapContainer>

                    <p className="s5-map-hint">
                        {!data.city_id
                            ? 'برای نمایش محله‌ها روی نقشه، یک شهر را انتخاب کنید.'
                            : drawMode
                                ? '🖊 موس را نگه دارید و محدوده دلخواه را رسم کنید.'
                                : homePin
                                    ? 'محل خانه شما ثبت شد. محله‌های بیشتری را می‌توانید انتخاب کنید.'
                                    : 'روی نقشه کلیک کنید تا محل خانه‌تان مشخص شود.'
                        }
                    </p>
                </div>
            </div>

            <div className="aaw-nav">
                <button className="aaw-btn aaw-btn-secondary" onClick={onBack}>→ مرحله قبل</button>
                <button className="aaw-btn aaw-btn-primary" onClick={handleNext}>مرحله بعد ←</button>
            </div>
        </div>
    );
}
